<template>
  <div class="recurrent-invoices-wrap">

    <RecurrentInvoiceModal 
      v-if="selectedRecurrentInvoice" 
      :invoice="selectedRecurrentInvoice" 
      @close="selectedRecurrentInvoice = null" 
      @updated-invoice="updateInvoiceTableItem"
    />
    
    <div v-b-toggle.collapse-recurrent class="recurrent-invoices-toggle">
      <span class="when-open"><font-awesome-icon icon="fa-solid fa-square-minus" /></span>
      <span class="when-closed"><font-awesome-icon icon="fa-solid fa-square-plus" /></span>
      {{ $gettext('You currently have') }} {{ recurrentInvoicesCount }} {{ $gettext('scheduled invoices') }}
    </div>
    <b-collapse id="collapse-recurrent" class="recurrent-invoices-collapse">
      <b-table 
        :class="['recurrent-invoices-table gk-table gk-table--headed']"
        :items="recurrentInvoices" 
        :fields="recurrentInvoicesFields"
        @row-clicked="rowClicked"
        show-empty
      >
        <template #cell(name)="data">
          <span v-if="data.item.name">{{ data.value }}</span>
          <span v-else>Task for {{ data.item.invoice.number }}</span>
        </template>

        <template #cell(next_date)="data">
          {{ data.item.next_date }}
        </template>

        <template #cell(iterations_left)="data">
          <span v-if="data.item.end_method !== 'endless'">{{ data.item.iterations_left }}</span>
          <span v-else>{{ $gettext('No end') }}</span>
        </template>

        <template #cell(recipient_customer)="data">
          {{ getRecurrentCustomerName(data.item.invoice.recipient_customer) }}
        </template>

        <template #cell(currency_total_amount)="data">
          € {{ data.item.invoice.total_amount | formatNumber }}
        </template>

        <template #cell(actions)="data">
          <div>
            <GekkoButton 
              icon="fa-solid fa-trash-can"
              class="text-danger"
              size="sm"
              variant="link"
              v-gk-confirm="{
                title: $gettext('Delete recurring invoice?'),
                message: $gettext('Are you sure you want to delete this invoice?'),
                options: {
                  variant: 'danger',
                  confirmText: $gettext('Delete'),
                },
                callback: deleteRecurrentInvoice,
                callbackParams: data.item
              }"
            />
          </div>
        </template>

        <template #empty="">
          {{ $gettext('You don\'t have any scheduled invoices yet.') }}
        </template>

        <template #table-busy="">
          <b-skeleton-table :rows="10" :columns="4" :table-props="{ bordered: false, striped: true }" :hide-header="true" />
        </template>

      </b-table>
    </b-collapse>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import InvoiceRecurrentService from '@/store/services/invoice.recurrent.service.js';
import EmptyState from "@/components/ui/EmptyState"
import GekkoButton from "@/components/GekkoButton";
import RecurrentInvoiceModal from "@/components/invoices/ui/RecurrentInvoiceModal"

export default {
  name: "RecurrentInvoicesTable",
  components: {
    EmptyState,
    GekkoButton,
    RecurrentInvoiceModal
  },
  data () {
    return {
      recurrentInvoices: [],
      recurrentCustomers: [],
      loading: false,
      firstLoad: true,
      filters: { 
        extended: 'True',
        active: 'True',
        company_profile: window.company_id
      },
      recurrentInvoicesFields: [
        { key: 'name', label: this.$gettext('Name'), tdClass: '' },
        { key: 'next_date', label: this.$gettext('Next delivery'), tdClass: '' },
        { key: 'iterations_left', label: this.$gettext('Instances left'), tdClass: '' },
        { key: 'recipient_customer', label: this.$gettext('Customer'), tdClass: '' },
        { key: 'currency_total_amount', label: this.$gettext('Amount'), tdClass: '' },
        { key: 'actions', label: '', tdClass: '' },
      ],
      selectedRecurrentInvoice: null
    }
  },
  computed: {
    ...mapGetters ({ 
      currentCompany: "main/getCurrentCompany",
    }),
    tableBusy () {
      return this.loading && this.firstLoad
    },
    recurrentInvoicesCount () {
      return (this.recurrentInvoices) ? this.recurrentInvoices.length : 0
    }
  },
  created () {
    InvoiceRecurrentService.customerList().then((response) => {
      this.recurrentCustomers = response.data
      this.loadRecurrentTasks()
    })
  },
  methods: {
    rowClicked (item) {
      this.selectedRecurrentInvoice = item
    },
    rowClass (item) {
      if(item && item.type) return 'recurrent-row-' + item.type + ' ' + 'recurrent-row-' + item.status
    },
    updateInvoiceTableItem (item) {
      for (var i = 0; i < this.recurrentInvoices.length; i++) {
        if (this.recurrentInvoices[i].id === item.id) {
          this.$set(this.recurrentInvoices, i, item)
          break;
        }
      }
    },
    deleteInvoiceTableItem (item) {
      for (var i = 0; i < this.recurrentInvoices.length; i++) {
        if (this.recurrentInvoices[i].id === item.id) {
          this.recurrentInvoices.splice(i, 1)
          break;
        }
      }
    },
    loadRecurrentTasks () {
      this.loading = true
      return InvoiceRecurrentService.list(this.filters).then((response) => {
        this.recurrentInvoices = response.data
        this.loading = false
        this.firstLoad = false
      });
    },
    getRecurrentCustomerName (customer_id) {
      const recurrent_customer = this.recurrentCustomers.find(({ id }) => id === customer_id)
      return (recurrent_customer) ? recurrent_customer.name : false
    },
    deleteRecurrentInvoice (invoice) {
      invoice.active = false
      invoice.company_profile = this.currentCompany.id;
      invoice.created_by = invoice.created_by.id;
      if (invoice.days.length == 0) {
        invoice.days = ''
      } else {
        invoice.days = invoice.days.join()
      }
      invoice.invoice = invoice.invoice.id
      InvoiceRecurrentService.update(invoice.id, invoice).then((response) => {
        this.deleteInvoiceTableItem(response.data)
      })
    }
  }
}
</script>

<style lang="scss">
.recurrent-invoices-wrap {
  margin-top: 0.5rem;
  margin-bottom: 1rem;
  background: #fcf3d9;
  border-radius: $radiusMd;
  overflow: hidden;
  .recurrent-invoices-toggle {
    padding: 0.5rem;
    font-size: 14px;
    color: #d6a00f;
    user-select: none;
    &.collapsed > .when-open,
    &.not-collapsed > .when-closed {
      display: none;
    }
    .when-open, .when-closed { margin-right: 0.5rem; }
  }
  .recurrent-invoices-collapse {
    background: #fcf3d9;
  }
  .recurrent-invoices-table {
    background: #fef9ec;
    font-size: 14px;
    thead {
      background: #fcf3d9;
      text-transform: none;
      color: $colorTextBase;
    }
    tbody {
      tr {
        &:hover {
          background: #e8f3de;
        }
      }
    }
  }
}
</style>